import React, { useState, useMemo } from 'react';
import { useIsMobile } from '../useIsMobile';
import { isAdmin } from './AdminDashboard';
import { calcAvgUsage, mergeUsage, getStatus, calcRecommendedOrder } from '../utils/inventoryAlerts';
import { calcRecipeBasedUsage } from '../utils/recipeUsage';
import IngredientVarianceTab from './IngredientVarianceTab';
import BiName from '../i18n/BiName';   // V100 한중 병기
import { useLang } from '../i18n/LangContext';   // V105 UI 다국어

const fmt = (n) => Math.round(n || 0).toLocaleString('ko-KR');

export default function InventoryManager({
  prices = [], orderHistory = [], inventory, onInventoryChange, canAccess, user,
  categories = [], payrollSettings = {}, onAddToPurchaseOrder,
  menuRecipeMap = {}, onMenuRecipeMapChange,
}) {
  const { t, lang } = useLang();                    // V105
  const Z = (ko, zh) => (lang === 'zh' ? zh : ko);   // V147 화면 문구
  // V147: 재고 상태 글자 (inventoryAlerts는 한국어로 준다)
  const sLabel = (st) => {
    if (lang !== 'zh' || !st) return st && st.label;
    const d = st.daysLeft !== undefined ? Math.floor(st.daysLeft) : null;
    if (st.icon === '🚨') return '没有库存';
    if (st.icon === '❓') return '没有数据';
    if (st.icon === '🔴') return '需要订货';
    if (st.icon === '⚠️') return `${d}天后用完`;
    return `还剩${d}天`;
  };
  const isMobile = useIsMobile();
  const [tab, setTab] = useState('status'); // status | analysis | settings
  const [search, setSearch] = useState('');
  const [editingItem, setEditingItem] = useState(null); // { name, currentStock, safetyStock, unit }
  const [sortBy, setSortBy] = useState('status'); // status | name | days
  const [usageSource, setUsageSource] = useState('auto'); // auto | orderHistory | recipe

  // ─── React Hooks 규칙: 모든 hook은 조건문 위에서 무조건 호출 ───
  // 발주 내역 기반 사용량
  const orderUsage = useMemo(() => calcAvgUsage(orderHistory, 3), [orderHistory]);
  // 레시피 + 메뉴 판매수량 기반 사용량 (B-3)
  const recipeUsage = useMemo(
    () => calcRecipeBasedUsage(categories, prices, payrollSettings?.expectedMonthlyServings),
    [categories, prices, payrollSettings?.expectedMonthlyServings]
  );
  // 사용 데이터 결정 (auto = 두 소스 병합, 큰 값 사용)
  const avgUsage = useMemo(() => {
    if (usageSource === 'orderHistory') return orderUsage;
    if (usageSource === 'recipe') return recipeUsage;
    return mergeUsage(orderUsage, recipeUsage);
  }, [usageSource, orderUsage, recipeUsage]);
  const hasOrderData = Object.keys(orderUsage).length > 0;
  const hasRecipeData = Object.keys(recipeUsage).length > 0;

  // 단가표의 모든 식재료 + 재고 데이터 합치기
  const allItems = useMemo(() => {
    const inv = inventory?.items || {};
    const itemMap = {};

    // 단가표 기준으로 목록 생성
    prices.forEach(p => {
      const u = avgUsage[p.name] || {};
      itemMap[p.name] = {
        name: p.name,
        name_zh: p.name_zh || '',        // V100 단가표에서 가져온 중국어명
        unit: p.unit || 'g',
        netG: p.net_g || 0,
        pricePerUnit: p.price || 0,
        category: p.category || '기타',
        currentStock: inv[p.name]?.currentStock ?? null,
        safetyStock: inv[p.name]?.safetyStock ?? 0,
        avgMonthly: u.avgMonthly || 0,
        avgDaily: u.avgDaily || 0,
        dataMonths: u.dataMonths || 0,
        usageSource: u.source || null,
      };
    });

    // 발주 내역/레시피에는 있지만 단가표에 없는 항목도 포함
    Object.keys(avgUsage).forEach(name => {
      if (!itemMap[name]) {
        const u = avgUsage[name];
        itemMap[name] = {
          name,
          unit: u.unit || '',
          netG: 0, pricePerUnit: 0, category: '기타',
          currentStock: inv[name]?.currentStock ?? null,
          safetyStock: inv[name]?.safetyStock ?? 0,
          avgMonthly: u.avgMonthly || 0,
          avgDaily: u.avgDaily || 0,
          dataMonths: u.dataMonths || 0,
          usageSource: u.source || null,
        };
      }
    });

    return Object.values(itemMap);
  }, [prices, inventory, avgUsage]);

  // 필터 + 정렬
  const filteredItems = useMemo(() => {
    let items = allItems.filter(i => !search || i.name.includes(search));
    if (sortBy === 'status') {
      const order = { '🚨': 0, '🔴': 1, '⚠️': 2, '🟡': 3, '✅': 4, '❓': 5 };
      items = items.sort((a, b) => {
        const sa = getStatus(a.currentStock ?? 0, a.safetyStock, a.avgDaily);
        const sb = getStatus(b.currentStock ?? 0, b.safetyStock, b.avgDaily);
        return (order[sa.icon] ?? 5) - (order[sb.icon] ?? 5);
      });
    } else if (sortBy === 'days') {
      items = items.sort((a, b) => {
        const da = a.avgDaily > 0 ? (a.currentStock ?? 0) / a.avgDaily : 9999;
        const db = b.avgDaily > 0 ? (b.currentStock ?? 0) / b.avgDaily : 9999;
        return da - db;
      });
    } else {
      items = items.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    }
    return items;
  }, [allItems, search, sortBy]);

  // 발주 필요 항목
  const needOrderItems = useMemo(() =>
    allItems.filter(i => {
      const status = getStatus(i.currentStock ?? 0, i.safetyStock, i.avgDaily);
      return status.icon === '🔴' || status.icon === '🚨' || status.icon === '⚠️';
    }), [allItems]);

  // 스탠다드 플랜 게이팅 — useMemo 호출 후에 위치 (Hooks 규칙)
  if (!canAccess('pro') && !isAdmin(user)) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: '14px', padding: '40px', textAlign: 'center' }}>
        <div style={{ fontSize: '52px' }}>📦</div>
        <h2 style={{ fontSize: '20px', fontWeight: '800' }}>{Z('재고 관리 & 적정 발주량 분석', '库存管理 & 建议订货量')}</h2>
        <p style={{ color: '#6b7280', lineHeight: 1.7, maxWidth: '320px' }}>
          {lang === 'zh' ? <><strong>标准版</strong>以上才能使用。请联系老板。</> : <><strong>스탠다드 플랜</strong> 이상 전용 기능이에요.<br />
          발주 내역 기반 사용량 자동 분석으로 재고 낭비를 줄여보세요.</>}
        </p>
      </div>
    );
  }

  // 재고 업데이트
  const updateStock = (name, field, value) => {
    const inv = { ...(inventory || {}), items: { ...(inventory?.items || {}) } };
    inv.items[name] = { ...(inv.items[name] || {}), [field]: Number(value) || 0 };
    onInventoryChange(inv);
  };

  const saveEdit = () => {
    if (!editingItem) return;
    const inv = { ...(inventory || {}), items: { ...(inventory?.items || {}) } };
    inv.items[editingItem.name] = {
      ...(inv.items[editingItem.name] || {}),
      currentStock: Number(editingItem.currentStock) || 0,
      safetyStock: Number(editingItem.safetyStock) || 0,
    };
    onInventoryChange(inv);
    setEditingItem(null);
  };

  // 추천 발주량을 발주서에 일괄 추가
  const sendAllToPurchaseOrder = () => {
    if (!onAddToPurchaseOrder) return;
    const recs = {};
    allItems.forEach(item => {
      const rec = calcRecommendedOrder(item);
      if (rec > 0) recs[item.name] = rec;
    });
    if (Object.keys(recs).length === 0) {
      alert(Z('추천 발주량이 0인 항목만 있습니다.', '没有需要订货的品项（建议订货量都是 0）。'));
      return;
    }
    onAddToPurchaseOrder(recs);
  };

  // 단일 항목 발주서 추가
  const sendOneToPurchaseOrder = (item) => {
    if (!onAddToPurchaseOrder) return;
    const rec = calcRecommendedOrder(item);
    if (rec <= 0) return;
    onAddToPurchaseOrder({ [item.name]: rec });
  };

  const TABS = [
    { id: 'status',   label: Z('재고 현황', '库存现状'),    icon: '📦' },
    { id: 'analysis', label: Z('적정 발주량', '建议订货量'),  icon: '📊' },
    { id: 'variance', label: Z('재료 분석', '食材分析'),    icon: '🥕' },
    { id: 'settings', label: Z('재고 설정', '库存设置'),    icon: '⚙️' },
  ];

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: '#f0f2f5' }}>

      {/* 탭 */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e5e7eb', display: 'flex', position: 'sticky', top: 0, zIndex: 10 }}>
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: isMobile ? '12px 14px' : '14px 22px', border: 'none', background: 'none', cursor: 'pointer',
            fontWeight: '700', fontSize: isMobile ? '12px' : '13px',
            color: tab === t.id ? '#1A56A0' : '#9ca3af',
            borderBottom: tab === t.id ? '2px solid #1A56A0' : '2px solid transparent',
            whiteSpace: 'nowrap', flexShrink: 0, transition: 'all 0.15s',
          }}>{t.icon} {t.label}</button>
        ))}
      </div>

      <div style={{ padding: isMobile ? '12px' : '20px' }}>

        {/* ── 발주 필요 알림 배너 */}
        {needOrderItems.length > 0 && (
          <div style={{
            background: '#F4F4F5', borderRadius: '12px', padding: '12px 16px',
            marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '10px',
          }}>
            <span style={{ fontSize: '20px' }}>🚨</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: '800', color: '#52525B', fontSize: '14px' }}>
                {Z(`발주 필요 ${needOrderItems.length}개 항목`, `需要订货 ${needOrderItems.length} 项`)}
              </div>
              <div style={{ fontSize: '12px', color: '#52525B', marginTop: '2px' }}>
                {needOrderItems.slice(0, 4).map(i => i.name).join(', ')}
                {needOrderItems.length > 4 && Z(` 외 ${needOrderItems.length - 4}개`, ` 等 ${needOrderItems.length} 项`)}
              </div>
            </div>
            <button onClick={() => setTab('analysis')} style={{
              padding: '7px 14px', borderRadius: '8px', border: 'none',
              background: '#52525B', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer',
            }}>{t('발주표 보기')}</button>
          </div>
        )}

        {/* ══ 탭: 재고 현황 ══ */}
        {tab === 'status' && (
          <div>
            {/* 검색 + 정렬 */}
            <div style={{ display: 'flex', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder={Z('🔍 재료명 검색', '🔍 搜索食材')}
                style={{ flex: 1, minWidth: '160px', padding: '9px 14px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', outline: 'none' }} />
              <select value={sortBy} onChange={e => setSortBy(e.target.value)}
                style={{ padding: '9px 12px', border: '1.5px solid #e5e7eb', borderRadius: '10px', fontSize: '13px', background: '#fff', cursor: 'pointer' }}>
                <option value="status">{t('상태순')}</option>
                <option value="days">{t('소진일순')}</option>
                <option value="name">{t('이름순')}</option>
              </select>
            </div>

            {/* 요약 카드 */}
            <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap' }}>
              {[
                { label: Z('전체 품목', '全部品项'), value: allItems.length, color: '#374151', bg: '#f9fafb' },
                { label: Z('발주 필요', '需要订货'), value: needOrderItems.length, color: '#52525B', bg: '#F4F4F5' },
                { label: Z('재고 입력됨', '已填库存'), value: allItems.filter(i => i.currentStock !== null).length, color: '#1A56A0', bg: '#EFF6FF' },
                { label: Z('사용 데이터', '有用量数据'), value: Object.keys(avgUsage).length, color: '#18181B', bg: '#FAFAFA' },
              ].map((c, i) => (
                <div key={i} style={{ flex: 1, minWidth: '100px', background: c.bg, borderRadius: '10px', padding: '12px 14px' }}>
                  <div style={{ fontSize: '11px', color: '#9ca3af' }}>{c.label}</div>
                  <div style={{ fontSize: '22px', fontWeight: '900', color: c.color }}>{c.value}</div>
                </div>
              ))}
            </div>

            {/* 재고 목록 테이블 */}
            <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
              {/* 헤더 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 80px 80px 70px' : '1fr 90px 90px 90px 90px 80px',
                gap: '0', background: '#1A56A0', padding: '10px 16px',
              }}>
                {[Z('재료명', '食材名称'), Z('현재 재고', '现有库存'), isMobile ? null : Z('안전재고', '安全库存'), isMobile ? null : Z('월 사용량', '月用量'), Z('소진 예상', '预计用完'), Z('상태', '状态')].filter(Boolean).map((h, i) => (
                  <div key={i} style={{ fontSize: '11px', fontWeight: '700', color: '#fff', textAlign: i > 0 ? 'center' : 'left' }}>{h}</div>
                ))}
              </div>

              {filteredItems.length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: '#bbb', fontSize: '13px' }}>
                  {prices.length === 0 ? Z('단가표에 식재료를 등록하면 자동으로 나타나요', '在单价表登记食材后会自动显示') : Z('검색 결과가 없어요', '没有搜索结果')}
                </div>
              ) : (
                filteredItems.map((item, idx) => {
                  const status = getStatus(item.currentStock ?? 0, item.safetyStock, item.avgDaily);
                  const isEditing = editingItem?.name === item.name;
                  return (
                    <div key={item.name} style={{
                      display: 'grid',
                      gridTemplateColumns: isMobile ? '1fr 80px 80px 70px' : '1fr 90px 90px 90px 90px 80px',
                      gap: '0', padding: '10px 16px',
                      borderBottom: idx < filteredItems.length - 1 ? '1px solid #f0f0f0' : 'none',
                      background: isEditing ? '#f0f9ff' : idx % 2 === 0 ? '#fff' : '#fafafa',
                      alignItems: 'center',
                    }}>
                      {/* 재료명 */}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: '#1a1a1a' }}>
                          <BiName ko={item.name} zh={item.name_zh} />
                        </div>
                        <div style={{ fontSize: '10px', color: '#9ca3af' }}>{t(item.category)}</div>
                      </div>

                      {/* 현재 재고 */}
                      <div style={{ textAlign: 'center' }}>
                        {isEditing ? (
                          <input type="number" value={editingItem.currentStock}
                            onChange={e => setEditingItem({...editingItem, currentStock: e.target.value})}
                            style={{ width: '70px', padding: '4px 6px', border: '1.5px solid #1A56A0', borderRadius: '6px', fontSize: '12px', textAlign: 'right' }} />
                        ) : (
                          <button onClick={() => setEditingItem({ name: item.name, currentStock: item.currentStock ?? 0, safetyStock: item.safetyStock })}
                            style={{ background: item.currentStock !== null ? '#EFF6FF' : '#f9fafb', border: '1px dashed ' + (item.currentStock !== null ? '#1A56A0' : '#d1d5db'), borderRadius: '6px', padding: '4px 8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', color: item.currentStock !== null ? '#1A56A0' : '#9ca3af' }}>
                            {item.currentStock !== null ? `${fmt(item.currentStock)}${item.unit}` : Z('입력', '填写')}
                          </button>
                        )}
                      </div>

                      {/* 안전재고 (PC만) */}
                      {!isMobile && (
                        <div style={{ textAlign: 'center', fontSize: '12px', color: '#9ca3af' }}>
                          {isEditing ? (
                            <input type="number" value={editingItem.safetyStock}
                              onChange={e => setEditingItem({...editingItem, safetyStock: e.target.value})}
                              style={{ width: '70px', padding: '4px 6px', border: '1.5px solid #1A56A0', borderRadius: '6px', fontSize: '12px', textAlign: 'right' }} />
                          ) : (
                            <span>{item.safetyStock > 0 ? `${fmt(item.safetyStock)}${item.unit}` : '—'}</span>
                          )}
                        </div>
                      )}

                      {/* 월 사용량 (PC만) */}
                      {!isMobile && (
                        <div style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                          {item.avgMonthly > 0 ? `${fmt(item.avgMonthly)}${item.unit}` : '—'}
                          {item.dataMonths > 0 && <div style={{ fontSize: '10px', color: '#bbb' }}>{Z(`${item.dataMonths}개월 기준`, `按 ${item.dataMonths} 个月`)}</div>}
                        </div>
                      )}

                      {/* 소진 예상일 */}
                      <div style={{ textAlign: 'center', fontSize: '12px', fontWeight: '600', color: status.color }}>
                        {item.currentStock !== null && item.avgDaily > 0
                          ? status.daysLeft !== undefined ? Z(`${Math.floor(status.daysLeft)}일`, `${Math.floor(status.daysLeft)}天`) : Z('충분', '充足')
                          : '—'}
                      </div>

                      {/* 상태 */}
                      <div style={{ textAlign: 'center' }}>
                        {isEditing ? (
                          <div style={{ display: 'flex', gap: '4px' }}>
                            <button onClick={saveEdit} style={{ padding: '4px 8px', borderRadius: '6px', border: 'none', background: '#FF6F0F', color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}>{t('저장')}</button>
                            <button onClick={() => setEditingItem(null)} style={{ padding: '4px 6px', borderRadius: '6px', border: '1px solid #e5e7eb', background: '#fff', fontSize: '11px', cursor: 'pointer' }}>✕</button>
                          </div>
                        ) : (
                          <span style={{ fontSize: '11px', background: status.bg, color: status.color, padding: '3px 8px', borderRadius: '8px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                            {status.icon} {isMobile ? '' : sLabel(status)}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {prices.length === 0 && (
              <div style={{ marginTop: '12px', background: '#F4F4F5', borderRadius: '10px', padding: '12px 16px', fontSize: '13px', color: '#52525B' }}>
                {Z('💡 단가표에 식재료를 등록하면 재고 관리가 자동으로 연동돼요', '💡 在单价表登记食材后，库存管理会自动同步')}
              </div>
            )}
          </div>
        )}

        {/* ══ 탭: 적정 발주량 분석표 ══ */}
        {tab === 'analysis' && (
          <div>
            <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '14px 16px', marginBottom: '12px' }}>
              <div style={{ fontWeight: '800', color: '#1A56A0', fontSize: '14px', marginBottom: '4px' }}>📊 {Z('적정 발주량 계산 방법', '建议订货量怎么算')}</div>
              <div style={{ fontSize: '12px', color: '#1D4ED8', lineHeight: 1.7 }}>
                {Z('월 평균 사용량 × 1.5배 - 현재 재고 = 추천 발주량', '月平均用量 × 1.5 − 现有库存 = 建议订货量')}
                <br />{Z('🔴 빨간 항목은 즉시 발주가 필요해요', '🔴 红色的品项需要马上订货')}
              </div>
            </div>

            {/* 사용량 데이터 소스 토글 */}
            <div style={{
              background: '#fff', borderRadius: '12px', padding: '10px 14px', marginBottom: '12px',
              boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
            }}>
              <div style={{ fontSize: '11px', color: '#6b7280', marginBottom: 6, fontWeight: 700 }}>
                📐 {Z('사용량 데이터 소스', '用量数据来源')}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {[
                  { id: 'auto', label: Z('🤝 자동 (둘 중 큰 값)', '🤝 自动（取较大值）'), enabled: true },
                  { id: 'orderHistory', label: Z(`📋 발주 내역${hasOrderData ? '' : ' (없음)'}`, `📋 订货记录${hasOrderData ? '' : '（无）'}`), enabled: hasOrderData },
                  { id: 'recipe', label: Z(`📋 레시피${hasRecipeData ? '' : ' (예상수량 미입력)'}`, `📋 配方${hasRecipeData ? '' : '（未填预计销量）'}`), enabled: hasRecipeData },
                ].map(opt => (
                  <button key={opt.id} onClick={() => opt.enabled && setUsageSource(opt.id)}
                    disabled={!opt.enabled}
                    style={{
                      padding: '6px 12px', borderRadius: 7, fontSize: 11, fontWeight: 700,
                      border: '1.5px solid', borderColor: usageSource === opt.id ? '#1A56A0' : '#e5e7eb',
                      background: usageSource === opt.id ? '#1A56A0' : '#fff',
                      color: usageSource === opt.id ? '#fff' : (opt.enabled ? '#374151' : '#bbb'),
                      cursor: opt.enabled ? 'pointer' : 'not-allowed',
                      opacity: opt.enabled ? 1 : 0.5,
                    }}>{opt.label}</button>
                ))}
              </div>
              <div style={{ fontSize: 10, color: '#9ca3af', marginTop: 6 }}>
                {Z('💡 레시피 기반: 메뉴 가격 결정 도우미 → "월 예상 총 판매수량" 입력 시 자동 계산', '💡 按配方：在菜品定价助手里填写「每月预计总销量」后自动计算')}
              </div>
            </div>

            {/* 발주서로 일괄 추가 */}
            {onAddToPurchaseOrder && allItems.filter(i => calcRecommendedOrder(i) > 0).length > 0 && (
              <div style={{
                background: '#FF6F0F', borderRadius: '12px', padding: '12px 14px', marginBottom: '12px',
                display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
              }}>
                <span style={{ fontSize: 22 }}>📦</span>
                <div style={{ flex: 1, minWidth: 140 }}>
                  <div style={{ fontWeight: 800, fontSize: 13, color: '#fff' }}>
                    {Z('추천 발주량 일괄 추가', '一键加入建议订货量')}
                  </div>
                  <div style={{ fontSize: 11, color: '#E4E4E7', marginTop: 2 }}>
                    {Z(`${allItems.filter(i => calcRecommendedOrder(i) > 0).length}개 항목을 발주서에 한 번에 추가`, `把 ${allItems.filter(i => calcRecommendedOrder(i) > 0).length} 项一次加到订货单`)}
                  </div>
                </div>
                <button onClick={sendAllToPurchaseOrder} style={{
                  padding: '8px 14px', borderRadius: 8, border: 'none',
                  background: '#fff', color: '#18181B', fontWeight: 800, fontSize: 12, cursor: 'pointer',
                }}>📤 {Z('발주서로 보내기', '加到订货单')}</button>
              </div>
            )}

            <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)' }}>
              {/* 헤더 */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr 70px 80px 60px' : '1fr 90px 90px 90px 100px 70px',
                background: '#1A56A0', padding: '10px 16px',
              }}>
                {[Z('재료명', '食材名称'), Z('현재 재고', '现有库存'), isMobile ? null : Z('월 평균 사용', '月平均用量'), isMobile ? null : Z('1.5개월치', '1.5个月用量'), Z('추천 발주량', '建议订货量'), Z('발주', '订货')].filter(Boolean).map((h, i) => (
                  <div key={i} style={{ fontSize: '11px', fontWeight: '700', color: '#fff', textAlign: i === 0 ? 'left' : 'center' }}>{h}</div>
                ))}
              </div>

              {allItems.filter(i => i.avgMonthly > 0).length === 0 ? (
                <div style={{ padding: '32px', textAlign: 'center', color: '#bbb' }}>
                  <div style={{ fontSize: '32px', marginBottom: '8px' }}>📦</div>
                  <div style={{ fontSize: '13px' }}>{Z('사용량 데이터가 없어요', '还没有用量数据')}</div>
                  <div style={{ fontSize: '12px', color: '#d1d5db', marginTop: '4px' }}>
                    {Z('발주서 저장 또는 메뉴 가격 결정 도우미에 "월 예상 판매수량" 입력 시 자동 분석', '保存订货单，或在菜品定价助手里填写「每月预计销量」后自动分析')}
                  </div>
                </div>
              ) : (
                allItems
                  .filter(i => i.avgMonthly > 0)
                  .sort((a, b) => {
                    const ra = calcRecommendedOrder(a);
                    const rb = calcRecommendedOrder(b);
                    if (ra > 0 && rb <= 0) return -1;
                    if (ra <= 0 && rb > 0) return 1;
                    return b.avgMonthly - a.avgMonthly;
                  })
                  .map((item, idx, arr) => {
                    const recommended = calcRecommendedOrder(item);
                    const needOrder = recommended > 0;
                    const status = getStatus(item.currentStock ?? 0, item.safetyStock, item.avgDaily);
                    const target = Math.ceil(item.avgMonthly * 1.5);
                    const sourceIcon = item.usageSource === 'recipe' ? '📋' : item.usageSource === 'orderHistory' ? '📦' : '';
                    return (
                      <div key={item.name} style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '1fr 70px 80px 60px' : '1fr 90px 90px 90px 100px 70px',
                        padding: '11px 16px', alignItems: 'center',
                        borderBottom: idx < arr.length - 1 ? '1px solid #f0f0f0' : 'none',
                        background: needOrder ? (status.icon === '🚨' || status.icon === '🔴' ? '#fff5f5' : '#FAFAFA') : '#fff',
                      }}>
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <span style={{ fontSize: '13px', fontWeight: '700' }}><BiName ko={item.name} zh={item.name_zh} /></span>
                            {needOrder && <span style={{ fontSize: '10px', background: status.bg, color: status.color, padding: '1px 6px', borderRadius: '6px', fontWeight: '700' }}>{status.icon}</span>}
                          </div>
                          <div style={{ fontSize: '10px', color: '#9ca3af' }}>
                            {t(item.category)}
                            {sourceIcon && (
                              <span style={{ marginLeft: 6, color: '#9ca3af' }}>
                                {item.usageSource === 'recipe' ? Z('· 레시피 추정', '· 按配方估算') : Z('· 발주내역', '· 订货记录')}
                              </span>
                            )}
                          </div>
                        </div>
                        <div style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                          {item.currentStock !== null ? `${fmt(item.currentStock)}${item.unit}` : <span style={{ color: '#bbb' }}>{t('미입력')}</span>}
                        </div>
                        {!isMobile && (
                          <div style={{ textAlign: 'center', fontSize: '12px', color: '#6b7280' }}>
                            {fmt(item.avgMonthly)}{item.unit}
                          </div>
                        )}
                        {!isMobile && (
                          <div style={{ textAlign: 'center', fontSize: '12px', color: '#374151', fontWeight: '600' }}>
                            {fmt(target)}{item.unit}
                          </div>
                        )}
                        <div style={{ textAlign: 'center' }}>
                          {recommended > 0 ? (
                            <span style={{
                              display: 'inline-block', padding: '4px 10px', borderRadius: '8px',
                              background: status.icon === '🚨' || status.icon === '🔴' ? '#52525B' : '#EA580C',
                              color: '#fff', fontSize: '12px', fontWeight: '800',
                            }}>
                              {fmt(recommended)}{item.unit}
                            </span>
                          ) : (
                            <span style={{ fontSize: '12px', color: '#18181B', fontWeight: '700' }}>✅ {Z('충분', '充足')}</span>
                          )}
                        </div>
                        <div style={{ textAlign: 'center' }}>
                          {recommended > 0 && onAddToPurchaseOrder && (
                            <button onClick={() => sendOneToPurchaseOrder(item)}
                              title={Z('발주서에 추가', '加到订货单')}
                              style={{
                                padding: '4px 8px', borderRadius: 6, border: '1.5px solid #FF6F0F',
                                background: '#fff', color: '#18181B', fontSize: 11, fontWeight: 800,
                                cursor: 'pointer',
                              }}>+ {t('발주')}</button>
                          )}
                        </div>
                      </div>
                    );
                  })
              )}
            </div>
          </div>
        )}

        {/* ══ 탭: 재고 설정 ══ */}
        {tab === 'variance' && (
          <IngredientVarianceTab
            user={user}
            categories={categories}
            prices={prices}
            inventory={inventory}
            menuRecipeMap={menuRecipeMap}
            onMenuRecipeMapChange={onMenuRecipeMapChange}
          />
        )}

        {tab === 'settings' && (
          <div>
            <div style={{ background: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '14px' }}>
              <div style={{ padding: '14px 18px', borderBottom: '1px solid #f0f0f0', fontWeight: '800', fontSize: '15px', color: '#374151' }}>
                ⚙️ {Z('안전재고 & 발주 기준 설정', '安全库存 & 订货标准')}
              </div>
              <div style={{ padding: '14px 18px' }}>
                <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '14px', lineHeight: 1.7 }}>
                  {Z('안전재고: 이 수량 이하가 되면 즉시 발주 알림', '安全库存：低于这个数量就提醒订货')}<br />
                  {Z('(미설정 시 소진 7일 전 기준으로 경고)', '（没设置的话，预计 7 天内用完时提醒）')}
                </div>

                {filteredItems.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#bbb', padding: '20px', fontSize: '13px' }}>{Z('단가표에 식재료를 등록하세요', '请先在单价表登记食材')}</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {filteredItems.slice(0, 30).map(item => (
                      <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', background: '#f9fafb', borderRadius: '10px' }}>
                        <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#374151' }}><BiName ko={item.name} zh={item.name_zh} /></span>
                        <span style={{ fontSize: '11px', color: '#9ca3af', width: '30px', textAlign: 'right' }}>{item.unit}</span>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{Z('안전재고', '安全库存')}</span>
                          <input type="number" min="0"
                            value={(inventory?.items?.[item.name]?.safetyStock) || ''}
                            placeholder="0"
                            onChange={e => updateStock(item.name, 'safetyStock', e.target.value)}
                            style={{ width: '70px', padding: '6px 8px', border: '1.5px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', textAlign: 'right' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11px', color: '#9ca3af' }}>{t('현재')}</span>
                          <input type="number" min="0"
                            value={(inventory?.items?.[item.name]?.currentStock) ?? ''}
                            placeholder="0"
                            onChange={e => updateStock(item.name, 'currentStock', e.target.value)}
                            style={{ width: '70px', padding: '6px 8px', border: '1.5px solid #e5e7eb', borderRadius: '7px', fontSize: '12px', textAlign: 'right' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div style={{ background: '#EFF6FF', borderRadius: '12px', padding: '14px 16px', fontSize: '13px', color: '#1D4ED8', lineHeight: 1.8 }}>
              <div style={{ fontWeight: '700', marginBottom: '6px' }}>💡 {Z('재고 관리 팁', '库存管理小提示')}</div>
              {lang === 'zh' ? <>
              🔴 <strong>需要订货</strong> — 现有库存低于安全库存<br />
              ⚠️ <strong>注意</strong> — 预计 7 天内用完<br />
              🟡 <strong>留意</strong> — 预计 14 天内用完<br />
              ✅ <strong>充足</strong> — 够用 14 天以上<br />
              ❓ <strong>没有数据</strong> — 没有订货记录，无法分析</> : <>
              🔴 <strong>발주 필요</strong> — 현재 재고가 안전재고 이하<br />
              ⚠️ <strong>주의</strong> — 7일 이내 소진 예상<br />
              🟡 <strong>확인</strong> — 14일 이내 소진 예상<br />
              ✅ <strong>충분</strong> — 14일 이상 재고 보유<br />
              ❓ <strong>데이터 없음</strong> — 발주 내역이 없어 분석 불가</>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
